/* eslint-disable max-lines */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { gql } from '@apollo/client';
import { useTranslation } from '@modernloop/i18n';
import { FCWithFragments, MaybeTooltip, VirtualList, VirtualListProps } from '@modernloop/shared/components';
import { useFlag } from '@modernloop/shared/feature-flag';
import { CheckIcon, CrossIcon, LinkIcon, ModulesIcon } from '@modernloop/shared/icons';
import {
  Autocomplete,
  AutocompleteChangeDetails,
  AutocompleteChangeReason,
  AutocompleteOwnerState,
  AutocompleteRenderGetTagProps,
  AutocompleteRenderGroupParams,
  AutocompleteRenderOptionState,
  Avatar,
  Box,
  Chip,
  FilterOptionsState,
  IconButton,
  ListItem,
  ListSubheader,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import { useDebouncedCallback } from 'use-debounce';

import {
  BaseEditInterviewSeatModuleEmployeeSearchV2Fragment,
  BaseEditInterviewSeatModule_LinkableInterviewsFragment,
  BaseEditInterviewSeatModule_LinkedInterviewFragment,
  BaseEditInterviewSeatModule_SeatFragment,
  BaseReadInterviewSeatModule_SeatFragment,
  EmployeeFilterFragment,
  InterviewModule,
  InterviewModulesOrderByProperty,
  JobStageInterviewSeatType,
  Sort,
  useEmployeesAndModulesSearchV2LazyQuery,
} from 'src/generated/mloop-graphql';

import { EmployeeFilter } from 'src/entities/InterviewPlan/InterviewSeat/ModuleMemberFilters';
import { InterviewModuleFragment } from 'src/entities/Select/InterviewModulesSelect';

import { JobStageInterviewSeat } from 'src/store/slices/job-stage-interview-seat';

import { DEBOUNCE_TIMEOUT, PAGE_SIZE } from 'src/constants';

import BaseReadInterviewSeatModule from './BaseReadInterviewSeatModule';
import { OptionComponent } from './OptionComponent';
import SeatSettings from './SeatSettings';
import {
  jobStageInterviewSeatToModuleSeatUpdate,
  moduleSeatFragmentToJobStageInterviewSeat,
} from './seatSettingsAdapters';
import { AutoCompleteOption, AutoCompleteOptionType, BaseEditInterviewSeatModuleProps as Props } from './types';

type Fragments = {
  seat: BaseEditInterviewSeatModule_SeatFragment | undefined;
  linkedInterview: BaseEditInterviewSeatModule_LinkedInterviewFragment | undefined;
  linkableInterviewsFragment: BaseEditInterviewSeatModule_LinkableInterviewsFragment[];
};

const BaseEditInterviewSeatModule: FCWithFragments<Fragments, Props> = ({
  seat,
  seatIndex,
  linkableInterviewsFragment,
  linkedInterview,
  originalSeatId,
  onEditComplete,
  onDelete,
}): JSX.Element | null => {
  const { t } = useTranslation();
  const traineeFilteringEnabled = useFlag('trainee_attribute_filtering');
  const [searchTerm, setSearchTerm] = useState('');

  const [hasInterviewers, setHasInterviewers] = useState(false);

  const [selectedSeatOrModules, setSelectedSeatOrModules] = useState<AutoCompleteOption[]>([]);
  const [autoCompleteOptions, setAutoCompleteOptions] = useState<AutoCompleteOption[]>([]);
  const linkedInterviewOptionsRef = useRef<AutoCompleteOption[]>([]);

  const [searchEmployeesAndModules, { data: employeesAndModulesData, loading: loadingEmployeesAndModulesData }] =
    useEmployeesAndModulesSearchV2LazyQuery({
      fetchPolicy: 'network-only', // force this to fetch from the backend each time in case modules/members/attributes are added while in the scheduling flow
    });

  // eslint-disable-next-line modernloop/no-use-effect.cjs -- Effect intentionally performs a side effect after render; revisit before adding logic.
  useEffect(() => {
    // run the search once using an empty string when the component mounts
    searchEmployeesAndModules({
      variables: {
        employeeSearchInput: {
          search: '',
          isArchived: false,
          isAtsDisabled: false,
          isDirectoryDisabled: false,
          pageInput: {
            limit: 25,
            offset: 0,
          },
        },
        moduleSearchInput: {
          search: '',
          pageInput: {
            limit: 25,
            offset: 0,
          },
          orderBy: {
            property: InterviewModulesOrderByProperty.Name,
            direction: Sort.Asc,
          },
        },
      },
    });
  }, [searchEmployeesAndModules]);

  // eslint-disable-next-line modernloop/no-use-effect.cjs -- Effect synchronizes local state from external inputs; prefer derived state when possible.
  useEffect(() => {
    // populate the selectedSeatOrModules from the store
    const options: AutoCompleteOption[] = [];
    if (!seat) {
      return;
    }
    if (seat.freeformSeat) {
      if (seat.freeformSeat?.jobStageInterviewSeatEmployees) {
        seat.freeformSeat?.jobStageInterviewSeatEmployees.forEach((interviewSeatEmployee) => {
          if (interviewSeatEmployee?.employee) {
            options.push({ type: AutoCompleteOptionType.EMPLOYEE, employee: interviewSeatEmployee.employee });
          }
        });
      }
      setSelectedSeatOrModules(options);
      if (options.length) {
        setHasInterviewers(true);
      }
    }

    if (seat.moduleSeat && seat.moduleSeat.interviewModule) {
      setSelectedSeatOrModules([
        {
          type: AutoCompleteOptionType.INTERVIEW,
          interview: seat.moduleSeat.interviewModule,
        },
      ]);

      setHasInterviewers(false);
    }
    if (seat.linkedSeat && originalSeatId) {
      setSelectedSeatOrModules([
        { type: AutoCompleteOptionType.SEAT, originalSeatId, linkableInterview: linkedInterview },
      ]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // eslint-disable-next-line modernloop/no-use-effect.cjs -- Effect synchronizes local state from external inputs; prefer derived state when possible.
  useEffect(() => {
    let options: AutoCompleteOption[] = [];

    if (employeesAndModulesData && employeesAndModulesData?.employeeSearch?.items) {
      options = employeesAndModulesData?.employeeSearch?.items.map((emp) => ({
        type: AutoCompleteOptionType.EMPLOYEE,
        employee: emp,
      }));
    }

    if (employeesAndModulesData && employeesAndModulesData?.interviewModuleSearch?.items) {
      options = options.concat(
        employeesAndModulesData?.interviewModuleSearch?.items.map((inter) => ({
          type: AutoCompleteOptionType.INTERVIEW,
          interview: inter,
        }))
      );
    }
    setAutoCompleteOptions(options || []);
  }, [employeesAndModulesData]);

  // eslint-disable-next-line modernloop/no-use-effect.cjs -- Effect intentionally performs a side effect after render; revisit before adding logic.
  useEffect(() => {
    const options: AutoCompleteOption[] = [];

    linkableInterviewsFragment.forEach((interview) => {
      interview.jobStageInterviewSeats?.forEach((s) => {
        // Do not allow linking linked seats.
        if (s.type === JobStageInterviewSeatType.Linked) {
          return;
        }

        options.push({
          type: AutoCompleteOptionType.SEAT,
          originalSeatId: s.id,
          linkableInterview: interview,
        });
      });
    });

    linkedInterviewOptionsRef.current = options;
  }, [linkableInterviewsFragment]);

  const getOptionLabel = (option: AutoCompleteOption) => {
    if (option.type === 'employee') {
      return option.employee?.fullName ?? '';
    }
    if (option.type === AutoCompleteOptionType.INTERVIEW) {
      return option.interview?.name ?? '';
    }
    return `${option.linkableInterview?.name ?? ''}`;
  };

  const renderOption = (
    props: React.HTMLAttributes<HTMLLIElement>,
    option: AutoCompleteOption,
    state: AutocompleteRenderOptionState
  ) => {
    if (option.type === AutoCompleteOptionType.INTERVIEW) {
      if (!option.interview?.name) {
        return null;
      }

      return (
        <ListItem {...props}>
          <OptionComponent
            selected={state.selected}
            hasInterviewers={hasInterviewers}
            name={option.interview?.name}
            type={option.type}
          />
        </ListItem>
      );
    }

    if (option.type === AutoCompleteOptionType.EMPLOYEE) {
      if (!option.employee?.fullName && !option.employee?.email) {
        return null;
      }

      const tooltipTitle = !option.employee?.timezone
        ? t('hiring-dashboard-schedule:schedule.tooltip.interviewerTimezoneMissing')
        : undefined;

      return (
        <Tooltip title={tooltipTitle} placement="top">
          <Box>
            <ListItem {...props}>
              <OptionComponent
                email={option.employee.email}
                selected={state.selected}
                hasInterviewers={hasInterviewers}
                name={option.employee.fullName || option.employee?.email}
                type={option.type}
                photoUrl={option.employee.internalProfilePictureUrl ?? undefined}
              />
            </ListItem>
          </Box>
        </Tooltip>
      );
    }

    // Handling for linked seat
    let name = option.linkableInterview?.name;
    if (
      option.linkableInterview &&
      option.linkableInterview.jobStageInterviewSeats &&
      option.linkableInterview.jobStageInterviewSeats.length > 1 &&
      option &&
      option.originalSeatId
    ) {
      const linkedSeatIndex = option.linkableInterview.jobStageInterviewSeats.findIndex(
        (s) => s.id === option.originalSeatId
      );
      name = `${name} (Seat ${linkedSeatIndex + 1})`;
    }

    if (!name) {
      return null;
    }

    return (
      <ListItem {...props}>
        <OptionComponent selected={state.selected} hasInterviewers={hasInterviewers} type={option.type} name={name} />
      </ListItem>
    );
  };

  // FF on: edits buffer locally in draftSeat so nothing persists (the page auto-saves every
  // onUpdated) until the user commits with the checkmark. Selecting a module seeds the draft and
  // keeps the editor open so the inline settings panel appears immediately.
  // Null until the user actually edits; commits without edits fall back to the LIVE seat (see the
  // checkmark handler) so neither a no-op commit wipes settings nor a stale snapshot overwrites a
  // seat refreshed underneath the editor.
  const [draftSeat, setDraftSeat] = useState<BaseReadInterviewSeatModule_SeatFragment | null>(null);

  const bufferModuleSelection = useCallback(
    (option: AutoCompleteOption) => {
      if (!seat) {
        return;
      }
      const isSameModule = seat.moduleSeat?.interviewModuleId === option.interview?.id;
      setDraftSeat({
        id: seat.id,
        type: JobStageInterviewSeatType.Module,
        index: seatIndex,
        moduleSeat: {
          // Re-selecting the same module keeps the configured settings; a different module resets.
          ...(isSameModule ? seat.moduleSeat : {}),
          selectedEmployeeIds: isSameModule ? (seat.moduleSeat?.selectedEmployeeIds ?? []) : [],
          trainedConfig: isSameModule ? seat.moduleSeat?.trainedConfig : { selectedEmployeeIds: [] },
          interviewModuleId: option.interview?.id,
          interviewModule: option.interview as InterviewModule,
        },
      });
    },
    [seat, seatIndex]
  );

  const handleChange = useCallback(
    (
      event: React.ChangeEvent,
      values: AutoCompleteOption[],
      reason: AutocompleteChangeReason,
      details?: AutocompleteChangeDetails<AutoCompleteOption>
    ) => {
      if (reason === 'removeOption' || reason === 'clear') {
        setSelectedSeatOrModules(values);
        setSearchTerm('');
        return;
      }
      const index = values.findIndex(
        (value) => value.type === AutoCompleteOptionType.INTERVIEW || value.type === AutoCompleteOptionType.SEAT
      );

      if (
        details &&
        (details.option.type === AutoCompleteOptionType.INTERVIEW ||
          details.option.type === AutoCompleteOptionType.SEAT ||
          index !== -1)
      ) {
        setSelectedSeatOrModules([details.option]);
        setHasInterviewers(details.option.type === AutoCompleteOptionType.EMPLOYEE);

        if (traineeFilteringEnabled && details.option.type === AutoCompleteOptionType.INTERVIEW) {
          // Keep editing so the settings panel shows for the freshly selected module.
          bufferModuleSelection(details.option);
        } else if (
          details.option.type === AutoCompleteOptionType.INTERVIEW ||
          details.option.type === AutoCompleteOptionType.SEAT
        ) {
          onEditComplete([details.option]);
        }
        setSearchTerm('');
        return;
      }

      setSelectedSeatOrModules(values);
      setHasInterviewers(true);
      setSearchTerm('');
    },
    [traineeFilteringEnabled, bufferModuleSelection, onEditComplete, setSelectedSeatOrModules, setHasInterviewers]
  );

  const debouncedHandleInputChange = useDebouncedCallback((search: string) => {
    searchEmployeesAndModules({
      variables: {
        employeeSearchInput: {
          search,
          isArchived: false,
          isAtsDisabled: false,
          isDirectoryDisabled: false,
          pageInput: {
            limit: PAGE_SIZE,
            offset: 0,
          },
        },
        moduleSearchInput: {
          search,
          pageInput: {
            limit: PAGE_SIZE,
            offset: 0,
          },
          orderBy: {
            property: InterviewModulesOrderByProperty.Name,
            direction: Sort.Asc,
          },
        },
      },
    });
  }, DEBOUNCE_TIMEOUT);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event && event?.target?.value !== undefined && typeof event?.target?.value === 'string') {
      setSearchTerm(event.target.value);
      debouncedHandleInputChange(event.target.value);
    }
  };

  const handleTextInputBlur = useCallback(() => {
    if (selectedSeatOrModules.length === 0) {
      onDelete();
      return;
    }
    // FF on + module selected: keep the editor open so the inline settings panel stays interactive
    // (clicking a setting blurs the input). The user commits with the checkmark instead.
    if (traineeFilteringEnabled && selectedSeatOrModules[0]?.type === AutoCompleteOptionType.INTERVIEW) {
      return;
    }
    onEditComplete(selectedSeatOrModules);
  }, [traineeFilteringEnabled, selectedSeatOrModules, onDelete, onEditComplete]);

  const isOptionEqualToValue = (option: AutoCompleteOption, value: AutoCompleteOption) => {
    if (option.type !== value.type) {
      return false;
    }
    if (option.type === AutoCompleteOptionType.EMPLOYEE) {
      return option.employee?.id === value.employee?.id;
    }
    if (option.type === AutoCompleteOptionType.SEAT) {
      return (
        option.originalSeatId === value.originalSeatId && option.linkableInterview?.id === value.linkableInterview?.id
      );
    }
    return option.interview?.id === value.interview?.id;
  };

  const renderTags = (
    value: AutoCompleteOption[],
    getTagProps: AutocompleteRenderGetTagProps,
    ownerState: AutocompleteOwnerState<AutoCompleteOption, true, true, false, 'div'>
  ) => {
    const { inputFocused } = ownerState;
    return (
      <>
        {value.map((option, index) => {
          let avatar = <ModulesIcon />;
          let key = option.interview?.id;
          let label = option.interview?.name;

          if (option.type === AutoCompleteOptionType.EMPLOYEE) {
            avatar = (
              <Avatar
                alt={option.employee?.fullName ?? ''}
                src={option.employee?.internalProfilePictureUrl ?? undefined}
              />
            );
            key = option.employee?.id;
            label = option.employee?.fullName ?? undefined;
          } else if (option.type === AutoCompleteOptionType.SEAT) {
            // This is just a fallback logic because when user selects a linked seat we immediately save it.
            avatar = <LinkIcon />;
            key = option.originalSeatId;
            const linkedSeatIndex =
              option.linkableInterview?.jobStageInterviewSeats?.findIndex((s) => s.id === option.originalSeatId) ?? -1;
            label = `${option.linkableInterview?.name} (Seat ${linkedSeatIndex !== -1 ? linkedSeatIndex + 1 : ''})`;
          }

          if (!label) {
            return null;
          }

          return (
            <Tooltip
              key={key}
              title={
                option.type === AutoCompleteOptionType.EMPLOYEE ? (
                  <Stack direction="column">
                    <Typography>{option.employee?.fullName}</Typography>
                    <Typography>{option.employee?.email}</Typography>
                  </Stack>
                ) : (
                  ''
                )
              }
            >
              <Chip
                label={<MaybeTooltip title={label} label={label} />}
                variant={inputFocused ? 'outlined' : 'filled'}
                avatar={avatar}
                {...getTagProps({ index })}
              />
            </Tooltip>
          );
        })}
      </>
    );
  };

  const renderGroup = (params: AutocompleteRenderGroupParams) => [
    <ListSubheader disableSticky key={params.key} component="div" sx={{ padding: '8px 16px' }}>
      <Typography variant="caption" color="primary" fontWeight={600}>
        {params.group === AutoCompleteOptionType.EMPLOYEE &&
          t('hiring-dashboard-schedule:schedule.editSeat.groupIndividuals')}
        {params.group === AutoCompleteOptionType.INTERVIEW &&
          t('hiring-dashboard-schedule:schedule.editSeat.groupModules')}
        {params.group === AutoCompleteOptionType.SEAT &&
          t('hiring-dashboard-schedule:schedule.editSeat.groupLinkFromAnotherInterview')}
      </Typography>
    </ListSubheader>,
    params.children,
  ];

  const filterOptions = (options: AutoCompleteOption[], state: FilterOptionsState<AutoCompleteOption>) => {
    // the interview modules and employees are already filtered by the search term, but we still need to filter on the linked interview names
    const { inputValue } = state;

    return options
      .filter((option) => {
        switch (option.type) {
          case AutoCompleteOptionType.EMPLOYEE:
          case AutoCompleteOptionType.INTERVIEW:
            // these types are already filtered by the search query
            return true;
          case AutoCompleteOptionType.SEAT:
            // this type isn't filtered yet so go ahead and filter based on the inputValue
            return Boolean(option.linkableInterview?.name?.toLowerCase().includes(inputValue.toLowerCase()));
          default:
        }
        return false;
      })
      .filter((option) => {
        if (option.type === AutoCompleteOptionType.INTERVIEW || option.type === AutoCompleteOptionType.SEAT) {
          return true;
        }
        // eslint-disable-next-line prefer-destructuring
        const employee: BaseEditInterviewSeatModuleEmployeeSearchV2Fragment | undefined = option.employee;
        return !employee?.isAtsDisabled && employee?.atsId;
      });
  };

  // combine the linkedInterview seats with the searched-for results
  const options = [...linkedInterviewOptionsRef.current, ...autoCompleteOptions];
  const listboxProps: VirtualListProps = {
    getChildSize: (child: React.ReactNode) => {
      if (React.isValidElement(child) && child.type === ListSubheader) {
        return 32;
      }

      if (
        child &&
        React.isValidElement(child) &&
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        React.Children.only((child.props as any)?.children)?.props?.children?.props?.children?.props?.type &&
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        React.Children.only((child.props as any)?.children)?.props?.children?.props?.children?.props?.type ===
          AutoCompleteOptionType.EMPLOYEE
      ) {
        return 52;
      }

      return 32;
    },
  };

  const getOptionDisabled = (option: AutoCompleteOption) =>
    Boolean(option.type === AutoCompleteOptionType.EMPLOYEE && option.employee?.fullName && !option.employee?.timezone);

  // The settings panel reads from the local draft when one exists so edits are visible
  // immediately without persisting anything.
  const settingsSourceSeat = draftSeat ?? seat;

  // The inline trainee/attribute settings panel only applies to a module seat, and only once a
  // module is selected (the autocomplete shows the module chip while staying in edit mode).
  const showSeatSettings =
    traineeFilteringEnabled &&
    Boolean(settingsSourceSeat?.moduleSeat?.interviewModuleId) &&
    selectedSeatOrModules.length === 1 &&
    selectedSeatOrModules[0]?.type === AutoCompleteOptionType.INTERVIEW;

  const seatSettingsMembers = useMemo(
    () =>
      (settingsSourceSeat?.moduleSeat?.interviewModule?.interviewModuleMembers?.items?.filter(Boolean) ??
        []) as EmployeeFilterFragment[],
    [settingsSourceSeat?.moduleSeat?.interviewModule?.interviewModuleMembers?.items]
  );

  const seatSettingsSeat = useMemo(
    () =>
      settingsSourceSeat
        ? moduleSeatFragmentToJobStageInterviewSeat(settingsSourceSeat, seatIndex, seatSettingsMembers)
        : undefined,
    [settingsSourceSeat, seatIndex, seatSettingsMembers]
  );

  const handleSeatSettingsUpdated = useCallback(
    (updatedSeat: JobStageInterviewSeat) => {
      if (!seat) {
        return;
      }
      const update = jobStageInterviewSeatToModuleSeatUpdate(seat.id, updatedSeat);
      const updatedModuleSeat = update.moduleSeat;
      if (!updatedModuleSeat) {
        return;
      }
      // Buffer only; the checkmark commits via onEditComplete.
      setDraftSeat((current) => ({
        id: update.id,
        type: JobStageInterviewSeatType.Module,
        index: seatIndex,
        moduleSeat: {
          ...updatedModuleSeat,
          interviewModule:
            updatedModuleSeat.interviewModule ?? (current ?? seat)?.moduleSeat?.interviewModule ?? undefined,
        },
      }));
    },
    [seat, seatIndex]
  );

  return (
    <Stack spacing={1}>
      <Grid container alignItems="center" spacing={1} wrap="nowrap">
        <Grid sx={{ flexGrow: 1, width: 0 }}>
          <Box borderRadius="6px" padding="2px 8px" width="100%">
            <Autocomplete
              autoHighlight
              fullWidth
              multiple
              disableClearable
              clearOnBlur
              disableListWrap
              loading={loadingEmployeesAndModulesData}
              ListboxComponent={VirtualList as React.ComponentType<React.HTMLAttributes<HTMLElement>>}
              ListboxProps={listboxProps as React.HTMLAttributes<HTMLElement>}
              options={options}
              value={selectedSeatOrModules}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={
                    selectedSeatOrModules.length === 0
                      ? t('hiring-dashboard-schedule:schedule.textField.searchModulesOrIndividuals')
                      : ''
                  }
                  autoFocus
                  onBlur={handleTextInputBlur}
                />
              )}
              getOptionLabel={getOptionLabel}
              renderOption={renderOption}
              renderTags={renderTags}
              onChange={handleChange}
              isOptionEqualToValue={isOptionEqualToValue}
              groupBy={(option: AutoCompleteOption) => option.type}
              renderGroup={renderGroup}
              onInputChange={handleInputChange}
              inputValue={searchTerm}
              filterOptions={filterOptions}
              getOptionDisabled={getOptionDisabled}
              slotProps={{
                paper: {
                  variant: 'elevation',
                  elevation: 1,
                },
              }}
            />
          </Box>
        </Grid>
        <Grid>
          <Grid container alignItems="center" justifyContent="flex-end">
            {selectedSeatOrModules.length > 0 && (
              <IconButton
                color="primary"
                size="medium"
                sx={{
                  backgroundColor: (theme) => theme.palette.primary.main,
                  '&:hover': {
                    backgroundColor: (theme) => theme.palette.primary.dark,
                  },
                }}
                onClick={() =>
                  onEditComplete(
                    selectedSeatOrModules,
                    // The buffered module draft only applies while the selection is still a module;
                    // converting the seat to an employee or linked seat must use the rebuild path.
                    // With no edits buffered, commit the live seat as-is so existing configuration
                    // survives a no-op confirm.
                    traineeFilteringEnabled && selectedSeatOrModules[0]?.type === AutoCompleteOptionType.INTERVIEW
                      ? (draftSeat ??
                          (seat?.moduleSeat?.interviewModuleId
                            ? {
                                id: seat.id,
                                type: JobStageInterviewSeatType.Module,
                                index: seatIndex,
                                moduleSeat: seat.moduleSeat,
                              }
                            : undefined))
                      : undefined
                  )
                }
              >
                <CheckIcon sx={{ color: (theme) => theme.palette.primary.contrastText }} />
              </IconButton>
            )}
            {selectedSeatOrModules.length === 0 && (
              <IconButton onClick={onDelete}>
                <CrossIcon fontSize="inherit" />
              </IconButton>
            )}
          </Grid>
        </Grid>
      </Grid>
      {/* Inline trainee/attribute settings (FF on) render below the seat input. SeatSettings and its
          nested EmployeeFilter seed local selection state on mount only, so key by the module id to
          force a remount when the module changes — otherwise a stale prior-module selection can
          merge into the new module's seat update. */}
      {showSeatSettings && seatSettingsSeat && (
        <SeatSettings
          key={settingsSourceSeat?.moduleSeat?.interviewModuleId}
          interviewModuleMembers={seatSettingsMembers}
          seat={seatSettingsSeat}
          onUpdated={handleSeatSettingsUpdated}
        />
      )}
    </Stack>
  );
};

BaseEditInterviewSeatModule.fragments = {
  seat: gql`
    ${BaseReadInterviewSeatModule.fragments.seat}
    fragment BaseEditInterviewSeatModule_seat on JobStageInterviewSeat {
      id
      ...BaseReadInterviewSeatModule_seat
    }
  `,
  linkableInterviewsFragment: gql`
    fragment BaseEditInterviewSeatModule_linkableInterviews on JobStageInterview {
      id
      name
      jobStageInterviewSeats {
        id
        type
      }
    }
  `,
  linkedInterview: gql`
    fragment BaseEditInterviewSeatModule_linkedInterview on JobStageInterview {
      id
      name
      jobStageInterviewSeats {
        id
      }
    }
  `,
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const BaseEditInterviewSeatModuleQuery = gql`
  fragment BaseEditInterviewSeatModuleEmployeeSearchV2 on Employee {
    id
    fullName
    email
    orgId
    isDirectoryDisabled
    hasAtsId
    internalProfilePictureUrl
    timezone
    atsId
    isAtsDisabled
  }

  ${InterviewModuleFragment}
  ${EmployeeFilter.fragments?.interviewModuleMembers}
  fragment BaseEditInterviewSeatModuleInterviewModuleSearchV2 on InterviewModule {
    ...InterviewModule
    orgId
    membersCount
    membersTrainedCount
    membersInTrainingCount
    membersInReverseShadowCount
    membersInShadowCount
    membersInPendingApprovalCount
    graduateFromShadowApprovalType
    graduateFromReverseShadowApprovalType
    interviewModuleMembers(input: { pageInput: { limit: 500, offset: 0 } }) {
      items {
        interviewModuleId
        employeeId
        ...EmployeeFilter
      }
    }
  }

  query EmployeesAndModulesSearchV2(
    $employeeSearchInput: EmployeeSearchInput!
    $moduleSearchInput: InterviewModuleSearchInput!
  ) {
    employeeSearch(input: $employeeSearchInput) {
      items {
        ...BaseEditInterviewSeatModuleEmployeeSearchV2
      }
    }
    interviewModuleSearch(input: $moduleSearchInput) {
      items {
        ...BaseEditInterviewSeatModuleInterviewModuleSearchV2
      }
    }
  }
`;

export default BaseEditInterviewSeatModule;
